/**
 * What media the questionnaire asset intakes accept: the one upload policy in
 * shared-types (B23), which the upload route reads too. The ConceptAssetsPanel
 * file input, the ComposerCollectIntake pickers and the builder-chat composer
 * paste/drop gate all read it, so a file one surface accepts is never refused
 * by another, or by the server, for a reason the user cannot see.
 */

import {
  QUESTIONNAIRE_ASSET_UPLOAD_MIME_TYPES,
  QUESTIONNAIRE_TURN_ATTACHMENT_MIME_TYPES,
  isQuestionnaireAssetUploadMime,
  normalizeUploadMime,
  questionnaireAssetUploadMaxBytes,
  questionnaireAssetUploadRefusal,
} from "@digital-twins/shared-types";
import i18n from "@/i18n";
import { isSupportedVideoMime } from "@/components/chat/useChatFileIngest";

export { isSupportedVideoMime };

/** <input accept> value for every asset-media picker. */
export const ASSET_MEDIA_ACCEPT = QUESTIONNAIRE_ASSET_UPLOAD_MIME_TYPES.join(",");

/**
 * <input accept> value for the chat composer: the types a turn's words carry
 * (`QUESTIONNAIRE_TURN_ATTACHMENT_MIME_TYPES`, images and videos), so its
 * picker never offers a PDF a turn refuses.
 */
export const COMPOSER_MEDIA_ACCEPT = QUESTIONNAIRE_TURN_ATTACHMENT_MIME_TYPES.join(",");

/** True when a picked file can become a tested-asset media ref (collect intakes). */
export function isAcceptedAssetFile(file: File): boolean {
  return isQuestionnaireAssetUploadMime(file.type);
}

/**
 * The line saying why `file` cannot be uploaded (its type, or its size for
 * its kind), in the interface language; null when the policy takes it. The
 * builder checks before it sends the bytes.
 */
export function assetUploadRefusalLine(file: File): string | null {
  const refusal = questionnaireAssetUploadRefusal(file);
  if (refusal === null) return null;
  return i18n.t(`questionnaire.upload.${refusal}`, {
    ns: "app",
    name: file.name,
    maxMb: Math.round(questionnaireAssetUploadMaxBytes(file.type) / (1024 * 1024)),
  });
}

/**
 * Composer paste/drop gate (QuestionnaireBuilderChat): the files a turn's
 * words carry (`QUESTIONNAIRE_TURN_ATTACHMENT_MIME_TYPES`, which Nest
 * reads too), and the rest by name, which the caller tells (never a silent
 * drop, H02-AD's class).
 * - 'ignore': no files in the event, so the default text paste proceeds.
 * - 'media': the usable files to stage, and the files left out.
 */
export function gateComposerMediaFiles(
  files: Array<File>,
):
  | { kind: "ignore" }
  | {
      kind: "media";
      media: Array<File>;
      /** Turn types the composer carries; policy refusals are listed separately. */
      turnRefused: Array<File>;
      policyRefused: Array<File>;
    } {
  if (files.length === 0) return { kind: "ignore" };
  const carried = (file: File) =>
    QUESTIONNAIRE_TURN_ATTACHMENT_MIME_TYPES.includes(normalizeUploadMime(file.type));
  const media: Array<File> = [];
  const turnRefused: Array<File> = [];
  const policyRefused: Array<File> = [];
  for (const file of files) {
    if (!carried(file)) {
      turnRefused.push(file);
      continue;
    }
    if (questionnaireAssetUploadRefusal(file) !== null) {
      policyRefused.push(file);
      continue;
    }
    media.push(file);
  }
  return { kind: "media", media, turnRefused, policyRefused };
}
